import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Layout from './SellerNetSheet/components/Layout';
import NumField from './SellerNetSheet/components/NumField';
import { computeNetSheet, fmtUSD } from './SellerNetSheet/lib/math';

const INITIAL = {
  sale_price: 0,
  commission_rate: 6,
  loan_payoff: 0,
  concessions: 0,
  title_escrow: 0,
  recording: 0,
  transfer_tax: 0,
  prorated_taxes: 0,
  hoa: 0,
  warranty: 0,
  staging: 0,
  other: 0,
};

const SCOPED_CSS = `
  .sns-root { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; color: #092825; }
  .sns-root .sns-num::-webkit-outer-spin-button,
  .sns-root .sns-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .sns-root .sns-num { -moz-appearance: textfield; }
`;

export default function SellerNetSheetPage() {
  const [inputs, setInputs] = useState(INITIAL);
  const update = (patch) => setInputs((prev) => ({ ...prev, ...patch }));

  const result = useMemo(() => computeNetSheet(inputs), [inputs]);
  const negative = result.netProceeds < 0;
  const hasAnyInput = inputs.sale_price > 0;

  return (
    <Layout>
      <Helmet>
        <title>Free Seller Net Sheet · Estimate Seller Proceeds · I Need Numbers</title>
        <meta
          name="description"
          content="Free seller net sheet calculator for real estate agents. Estimate your seller's net proceeds after commission, loan payoff, concessions, and closing costs. No login, nothing saved."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <style>{SCOPED_CSS}</style>

      <div className="sns-root" data-testid="sns-root">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
          <div className="text-xs uppercase tracking-wider text-[#2FA163] font-semibold mb-2">
            Free tool · I Need Numbers
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#092825] mb-2">
            Seller Net Sheet
          </h1>
          <p className="text-slate-700 max-w-2xl">
            Estimate your seller&rsquo;s net proceeds after commission, loan payoff, and
            closing costs. Fill in what you know — everything&rsquo;s optional except the
            sale price. Nothing saved, nothing sent.
          </p>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* Inputs — left 3 cols */}
            <div className="lg:col-span-3" data-testid="sns-inputs">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#092825] mb-4">Sale details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumField
                    label="Sale price"
                    prefix="$"
                    value={inputs.sale_price}
                    onChange={(v) => update({ sale_price: v })}
                    help="The agreed-upon sale price of the home."
                    testId="sns-input-sale-price"
                  />
                  <NumField
                    label="Total commission rate"
                    suffix="%"
                    value={inputs.commission_rate}
                    onChange={(v) => update({ commission_rate: v })}
                    help="The total real estate commission percentage on this deal, before it's split between the listing and buyer sides. Typical range is 4.5% to 6% depending on market and negotiation."
                    testId="sns-input-commission-rate"
                  />
                </div>

                <h2 className="text-lg font-bold text-[#092825] mt-8 mb-4">Existing mortgage</h2>
                <NumField
                  label="Loan payoff amount"
                  prefix="$"
                  value={inputs.loan_payoff}
                  onChange={(v) => update({ loan_payoff: v })}
                  help="What the seller still owes on their existing mortgage(s), including any HELOCs or second liens. This is the single biggest deduction on most net sheets."
                  testId="sns-input-loan-payoff"
                />

                <h2 className="text-lg font-bold text-[#092825] mt-8 mb-4">Closing costs</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumField
                    label="Seller concessions to buyer"
                    prefix="$"
                    value={inputs.concessions}
                    onChange={(v) => update({ concessions: v })}
                    help="Dollars the seller agreed to contribute toward the buyer's closing costs, pre-paids, or repairs. Comes straight out of the seller's net."
                    testId="sns-input-concessions"
                  />
                  <NumField
                    label="Title & escrow fees"
                    prefix="$"
                    value={inputs.title_escrow}
                    onChange={(v) => update({ title_escrow: v })}
                    help="Combined closing costs charged by the title company / escrow agent. Varies by state — $1,500-$3,000 is typical for a mid-market home."
                  />
                  <NumField
                    label="Recording fees"
                    prefix="$"
                    value={inputs.recording}
                    onChange={(v) => update({ recording: v })}
                    help="County recording fees to register the deed transfer. Usually small — $100-$300."
                  />
                  <NumField
                    label="Transfer tax / doc stamps"
                    prefix="$"
                    value={inputs.transfer_tax}
                    onChange={(v) => update({ transfer_tax: v })}
                    help="State and/or county tax on the property transfer. Varies wildly by state; Florida's documentary stamp tax, for example, is $0.70 per $100 of sale price."
                  />
                  <NumField
                    label="Prorated property taxes"
                    prefix="$"
                    value={inputs.prorated_taxes}
                    onChange={(v) => update({ prorated_taxes: v })}
                    help="The seller's share of annual property taxes for the portion of the year they owned the home before closing. Can be a credit or a deduction — enter a positive number if seller owes at closing."
                  />
                  <NumField
                    label="HOA transfer / estoppel fees"
                    prefix="$"
                    value={inputs.hoa}
                    onChange={(v) => update({ hoa: v })}
                    help="One-time fees the HOA charges to transfer the property on its records. $200-$500 is typical where HOAs exist."
                  />
                  <NumField
                    label="Home warranty for buyer"
                    prefix="$"
                    value={inputs.warranty}
                    onChange={(v) => update({ warranty: v })}
                    help="If the seller offered to buy the buyer a home warranty, enter the cost here. Typical: $500-$700."
                  />
                  <NumField
                    label="Staging costs"
                    prefix="$"
                    value={inputs.staging}
                    onChange={(v) => update({ staging: v })}
                    help="What the seller paid to have the home staged for sale. Often not disclosed at closing but comes out of net proceeds all the same."
                  />
                  <NumField
                    label="Other costs"
                    prefix="$"
                    value={inputs.other}
                    onChange={(v) => update({ other: v })}
                    help="Any other deductions not covered above — seller-paid repairs, survey, inspection credits, attorney fees, etc."
                  />
                </div>
              </div>
            </div>

            {/* Sticky summary — right 2 cols */}
            <aside className="lg:col-span-2">
              <div className="lg:sticky lg:top-24 flex flex-col gap-5">
                <div
                  className="bg-white rounded-lg border border-slate-200 shadow-sm p-6"
                  data-testid="sns-summary"
                >
                  <h2 className="text-lg font-bold text-[#092825] mb-4">Breakdown</h2>

                  <div className="flex justify-between text-sm py-1.5 border-b border-slate-100">
                    <span className="text-slate-600">Sale price</span>
                    <span className="tabular-nums font-semibold">{fmtUSD(result.gross)}</span>
                  </div>

                  {result.breakdown.length === 0 && (
                    <p className="text-sm text-slate-500 italic py-3">
                      Enter values on the left — deductions will show up here.
                    </p>
                  )}
                  {result.breakdown.map((it) => (
                    <div
                      key={it.key}
                      className="flex justify-between text-sm py-1.5 border-b border-slate-100"
                      data-testid={`sns-line-${it.key}`}
                    >
                      <span className="text-slate-600">{it.label}</span>
                      <span className="tabular-nums text-red-600">−{fmtUSD(it.amount)}</span>
                    </div>
                  ))}

                  <div className="flex justify-between text-sm py-2 mt-1 border-t-2 border-slate-300 font-semibold">
                    <span className="text-slate-700">Total deductions</span>
                    <span
                      className="tabular-nums text-red-600"
                      data-testid="sns-total-deductions"
                    >
                      −{fmtUSD(result.totalDeductions)}
                    </span>
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                      Estimated net proceeds to seller
                    </div>
                    <div
                      className={
                        'text-3xl sm:text-4xl font-bold tabular-nums ' +
                        (negative ? 'text-red-600' : 'text-[#2FA163]')
                      }
                      data-testid="sns-net-proceeds"
                    >
                      {hasAnyInput ? fmtUSD(result.netProceeds) : '—'}
                    </div>
                    {negative && hasAnyInput && (
                      <p className="text-xs text-red-600 mt-2" data-testid="sns-underwater-note">
                        This sale doesn&rsquo;t cover the deductions. The seller would
                        need to bring funds to closing.
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-slate-100 border border-slate-200 rounded p-4 text-xs text-slate-600 leading-relaxed space-y-2">
                  <p>This is a starting-point estimate. Actual closing numbers come from the title company&rsquo;s settlement statement.</p>
                  <p>Prorations and state-specific fees can shift the number by a few thousand dollars. Always confirm with the title / escrow officer before quoting a final number to the seller.</p>
                  <p>Commission, concessions, and repairs are the three line items most commonly negotiated. Small percentage changes compound — a 0.5% commission reduction on a $500,000 home moves the net by $2,500.</p>
                </div>

                {/* Upgrade CTA */}
                <div
                  className="bg-white rounded-lg border border-[#2FA163]/30 shadow-md p-8"
                  data-testid="sns-upgrade-cta"
                >
                  <div className="text-xs uppercase tracking-wider text-[#2FA163] font-semibold mb-2">
                    Want to hand this to your seller?
                  </div>
                  <h3 className="text-lg font-bold text-[#092825] mb-3">
                    Turn this into a branded PDF with a 7-day free trial.
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    On the paid I Need Numbers platform, every net sheet saves to your
                    deal history and generates a beautiful branded PDF with your logo,
                    contact info, and colors — ready to email or hand to your seller at
                    the listing appointment.
                  </p>
                  <ul className="text-sm text-slate-700 space-y-1.5 mb-5">
                    <li className="flex gap-2">
                      <span className="text-[#2FA163] font-bold">✓</span>
                      Unlimited branded net sheets
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[#2FA163] font-bold">✓</span>
                      Saved deal history with notes
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[#2FA163] font-bold">✓</span>
                      AI coaching against your real numbers
                    </li>
                  </ul>
                  <Link
                    to="/pricing?source=seller-net-sheet&plan=pro"
                    data-testid="sns-trial-cta-btn"
                    className="inline-block w-full text-center text-white font-semibold px-6 py-3 rounded shadow transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #2FA163 0%, #286C4E 100%)' }}
                  >
                    Start my 7-day trial →
                  </Link>
                </div>
              </div>
            </aside>

          </div>
        </section>
      </div>
    </Layout>
  );
}
